import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import { Visibility, Download, Edit } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { projectService } from '../services/projectService';
import documentEditorService from '../services/documentEditor.service';
import { drugService } from '../services/drugService';
import PolicyDeniedPane from './access/PolicyDeniedPane';
import { parsePolicyDenialResponse } from '../utils/policyViewerDenial';
import {
  projectListPrimaryLabel,
  resolveProjectSponsorName,
  resolveProjectSponsorProtocolId,
  resolveProjectTrialAcronym,
} from '../utils/projectIdentityFields';

const ClinicalIntakeHistory = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [drugName, setDrugName] = useState('');
  const [deniedReason, setDeniedReason] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get drugId from query string
  const searchParams = new URLSearchParams(location.search);
  const drugId = searchParams.get('drug');

  useEffect(() => {
    if (drugId) {
      fetchStudies();
    } else {
      setStudies([]);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [drugId]);

  const fetchStudies = async () => {
    try {
      setLoading(true);
      setError(null);
      setDeniedReason(null);

      // Fetch drug name first — this is the request gated by Hub IAM policy
      // (project:open, which the backend also grants when the user has access to
      // any study inside this project). A 403 here means truly no access to this
      // project or anything inside it, so block the whole page.
      try {
        const drugResponse = await drugService.getDrug(drugId);
        setDrugName(drugResponse.name || 'Unknown Drug');
      } catch (err) {
        const denial = parsePolicyDenialResponse(err, {
          defaultAction: 'project:open',
          forbiddenFallbackReason: 'You do not have access to this project.',
        });
        if (denial) {
          setDeniedReason(denial.reason);
          setLoading(false);
          return;
        }
        console.error('Failed to fetch drug name:', err);
        setDrugName('Unknown Drug');
      }

      // Fetch studies — unfiltered: once the project itself is visible, every study
      // inside it is listed here, regardless of per-study access. Access to a
      // specific study is enforced separately when it's actually opened.
      const response = await projectService.listProjects({ drug: drugId });
      setStudies(Array.isArray(response.data) ? response.data : response);
    } catch (err) {
      setError('Failed to fetch studies');
      setStudies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Protocol, ICF, SAP handlers (navigate to editors, download, etc.)
  const handleViewProtocol = (study) => {
    navigate(`/document-editor/protocol/${study._id}?projectId=${study._id}`);
  };
  const handleEditProtocol = (study) => {
    navigate(`/new-form/${study._id}?projectId=${study._id}`);
  };
  const handleDownloadProtocol = async (study) => {
    alert('Download Protocol for ' + projectListPrimaryLabel(study));
  };

  const handleViewICF = (study) => {
    navigate(`/document-editor/icf/${study._id}?projectId=${study._id}`);
  };
  const handleEditICF = (study) => {
    navigate(`/icf-section-history/${study._id}?projectId=${study._id}`);
  };
  const handleDownloadICF = async (study) => {
    alert('Download ICF for ' + projectListPrimaryLabel(study));
  };

  const handleViewSAP = (study) => {
    navigate(`/document-editor/sap/${study._id}?projectId=${study._id}`);
  };
  const handleEditSAP = (study) => {
    navigate(`/sap-section-history/${study._id}?projectId=${study._id}`);
  };
  const handleDownloadSAP = async (study) => {
    alert('Download SAP for ' + projectListPrimaryLabel(study));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'primary';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'warning';
      case 'not_initiated':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading studies...
        </Typography>
      </Box>
    );
  }

  if (deniedReason) {
    return <PolicyDeniedPane reason={deniedReason} size="wide" />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!drugId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a drug from the sidebar to view its studies.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3} mt={2}>
      <Typography variant="h4" gutterBottom>
        {drugName ? `${drugName}` : 'Studies'}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sponsor Protocol Identifier</TableCell>
              <TableCell>Trial Acronym</TableCell>
              <TableCell>Sponsor Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Protocol</TableCell>
              <TableCell>Informed Consent Form</TableCell>
              <TableCell>Statistical Analysis Plan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {studies.length > 0 ? (
              (rowsPerPage > 0
                ? studies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : studies
              ).map((study) => (
                <TableRow key={study._id}>
                  <TableCell>
                    <Typography 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        textDecoration: 'underline',
                        '&:hover': { color: 'primary.dark' }
                      }}
                      onClick={() => navigate(`/clinical-intake?project=${study._id}&tab=protocol`)}
                    >
                      {resolveProjectSponsorProtocolId(study) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        textDecoration: 'underline',
                        '&:hover': { color: 'primary.dark' }
                      }}
                      onClick={() => navigate(`/clinical-intake?project=${study._id}&tab=protocol`)}
                    >
                      {resolveProjectTrialAcronym(study) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{resolveProjectSponsorName(study) || '—'}</TableCell>
                  <TableCell>{study.status}</TableCell>
                  <TableCell>{study.createdAt ? new Date(study.createdAt).toLocaleString() : 'N/A'}</TableCell>
                  {/* Protocol Actions */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={study.protocolStatus || 'draft'}
                        color={getStatusColor(study.protocolStatus || 'draft')}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <IconButton onClick={() => handleViewProtocol(study)} size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleDownloadProtocol(study)} size="small">
                        <Download />
                      </IconButton>
                      {(study.protocolStatus === 'draft' || !study.protocolStatus) && (
                        <IconButton onClick={() => handleEditProtocol(study)} size="small">
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  {/* ICF Actions */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={study.icfStatus || 'not_initiated'}
                        color={getStatusColor(study.icfStatus || 'not_initiated')}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <IconButton onClick={() => handleViewICF(study)} size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleDownloadICF(study)} size="small">
                        <Download />
                      </IconButton>
                      {(study.icfStatus === 'draft' || !study.icfStatus) && (
                        <IconButton onClick={() => handleEditICF(study)} size="small">
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  {/* SAP Actions */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={study.sapStatus || 'not_initiated'}
                        color={getStatusColor(study.sapStatus || 'not_initiated')}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <IconButton onClick={() => handleViewSAP(study)} size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleDownloadSAP(study)} size="small">
                        <Download />
                      </IconButton>
                      {(study.sapStatus === 'draft' || !study.sapStatus) && (
                        <IconButton onClick={() => handleEditSAP(study)} size="small">
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    No studies found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={studies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default ClinicalIntakeHistory; 